/**
 * Slate Violet Dark Theme
 *
 * Cool dark surfaces with violet primary actions.
 */
// Text tones the sidebar shares with the main ladder, declared once so raising
// one raises both. These were copied hex values, and a contrast fix to the
// ladder left the sidebar behind without anything failing.
const TEXT_HEADING = '#F8FAFC';
const TEXT_NEUTRAL = '#94A3B8';

const slateVioletDarkTheme = {
  name: 'slate-violet-dark',
  label: 'Slate Violet Dark',
  mode: 'dark',
  colorSet: 'slate-violet',

  colors: {
    canvas: '#060612',
    panelPrimary: '#1A1A3E',
    panelSecondary: '#252547',
    panelTertiary: '#2C2C52',
    panelQuaternary: '#3F3F6E',
    surfaceHover: '#35355E',

    borderSubtle: '#35355E',
    borderStrong: '#52528A',

    textHeading: TEXT_HEADING,
    textPrimary: '#E2E8F0',
    textSecondary: '#B6C2D1',
    textSupporting: '#A5B4C5',
    textNeutral: TEXT_NEUTRAL,
    textMuted: '#8D9AAD',
    textInverse: '#060612',

    accentPrimary: '#8B5CF6',
    accentPrimaryVibrant: '#A78BFA',
    accentSecondary: '#2DD4BF',
    accentSecondaryVibrant: '#5EEAD4',
    accentWarm: '#FBBF24',
    accentWarmVibrant: '#FCD34D',

    statusDanger: '#F87171',
    statusWarning: '#FBBF24',
    statusSuccess: '#34D399',
    statusInfo: '#A78BFA',

    gridline: 'rgba(167, 139, 250, 0.14)',
    focusOutline: 'rgba(139, 92, 246, 0.65)',
    overlay: 'rgba(6, 6, 18, 0.80)',

    headerBg: '#0A0A1E',
    sidebarBg: '#060612',
    cardBg: '#1A1A3E',
    inputBg: '#0A0A1E',
    dropdownBg: '#1A1A3E',

    navSectionHeader: TEXT_NEUTRAL,
    navItemText: '#CBD5E1',
    navItemHover: TEXT_HEADING,
    navItemHoverBg: 'rgba(139, 92, 246, 0.12)',
    navItemActive: '#A78BFA',
    navItemActiveBg: 'rgba(139, 92, 246, 0.18)',
    navIconDefault: TEXT_NEUTRAL,
    navIconHover: '#A78BFA',
    navIconActive: '#A78BFA',
    navDivider: 'rgba(148, 163, 184, 0.22)',

    tableEntity: '#E2E8F0',
    tableMetric: '#5EEAD4',
    tableBody: '#CBD5E1',
    tableSecondary: '#A5B4C5',
    tableTime: '#94A3B8',

    qualityP20: '#F87171',
    qualityP40: '#FB923C',
    qualityP60: '#FBBF24',
    qualityP80: '#6EE7B7',
    qualityP100: '#34D399',

    pillBgFrom: '#252547',
    pillBgTo: '#2C2C52',
    pillActiveBgFrom: '#35355E',
    pillActiveBgTo: '#3F3F6E',
    pillHoverBgFrom: '#2C2C52',
    pillHoverBgTo: '#35355E',

    tableHeaderBg: '#252547',
    tableRowHover: 'rgba(139, 92, 246, 0.10)',
    tableRowExpanded: 'rgba(45, 212, 191, 0.09)',
    buttonTextOnAccent: '#FFFFFF',

    statusDangerRgb: '248, 113, 113',
    statusSuccessRgb: '52, 211, 153',
    statusWarningRgb: '251, 191, 36',
    statusInfoRgb: '167, 139, 250',
    accentWarmRgb: '251, 191, 36',

    severityCriticalText: '#F87171',
    severityCriticalBg: 'rgba(248, 113, 113, 0.13)',
    severityCriticalBorder: 'rgba(248, 113, 113, 0.34)',
    severityCriticalGlow: '0 0 18px rgba(248, 113, 113, 0.28)',
    severityHighText: '#FBBF24',
    severityHighBg: 'rgba(251, 191, 36, 0.13)',
    severityHighBorder: 'rgba(251, 191, 36, 0.34)',
    severityHighGlow: '0 0 18px rgba(251, 191, 36, 0.28)',
    severityMediumText: '#A78BFA',
    severityMediumBg: 'rgba(167, 139, 250, 0.13)',
    severityMediumBorder: 'rgba(167, 139, 250, 0.34)',
    severityMediumGlow: '0 0 18px rgba(167, 139, 250, 0.28)',
    severityLowText: '#8B5CF6',
    severityLowBg: 'rgba(139, 92, 246, 0.13)',
    severityLowBorder: 'rgba(139, 92, 246, 0.34)',
    severityLowGlow: '0 0 18px rgba(139, 92, 246, 0.28)',
    severitySuccessText: '#34D399',
    severitySuccessBg: 'rgba(52, 211, 153, 0.13)',
    severitySuccessBorder: 'rgba(52, 211, 153, 0.34)',
    severitySuccessGlow: '0 0 8px rgba(52, 211, 153, 0.28)',

    borderMuted: '#35355E',
    borderMedium: '#3F3F6E',
    borderAccent: '#52528A',

    surfaceSubtle: 'rgba(226, 232, 240, 0.045)',
    surfaceMid: 'rgba(226, 232, 240, 0.075)',
    surfaceOverlayHover: 'rgba(139, 92, 246, 0.085)',

    scrollbarTrack: 'rgba(44, 44, 82, 0.42)',
    scrollbarThumb: 'rgba(148, 163, 184, 0.48)',
    scrollbarThumbHover: 'rgba(203, 213, 225, 0.62)',

    selectionBg: 'rgba(139, 92, 246, 0.30)',

    graphBg: '#060612',
    graphNodeBg: '#1A1A3E',
    graphNodeText: '#E2E8F0',
  },

  shadows: {
    card: '0 1px 2px rgba(0, 0, 0, 0.36), 0 12px 28px rgba(0, 0, 0, 0.42)',
    soft: '0 4px 16px rgba(0, 0, 0, 0.34), 0 18px 40px rgba(0, 0, 0, 0.38)',
    dropdown: '0 12px 32px rgba(0, 0, 0, 0.50), 0 1px 3px rgba(0, 0, 0, 0.42)',
    drawer: '-8px 0 24px rgba(0, 0, 0, 0.44), -1px 0 3px rgba(0, 0, 0, 0.36)',
  },
};

export default slateVioletDarkTheme;
